import React, { useEffect, useState } from "react";
import { Loader2, UserPlus, CreditCard, Undo2, PauseCircle, PlayCircle, Repeat, XCircle, LifeBuoy, Star, FileText } from "lucide-react";
import { api } from "../lib/api";
import { euro as eur, fmtDate } from "../lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";

const KINDS = {
  signup: [UserPlus, "bg-slate-100 text-slate-600"],
  payment: [CreditCard, "bg-emerald-100 text-emerald-700"],
  refund: [Undo2, "bg-red-100 text-red-600"],
  suspend: [PauseCircle, "bg-amber-100 text-amber-700"],
  reactivate: [PlayCircle, "bg-emerald-100 text-emerald-700"],
  plan_change: [Repeat, "bg-indigo-100 text-indigo-700"],
  cancel: [XCircle, "bg-red-100 text-red-600"],
  ticket: [LifeBuoy, "bg-sky-100 text-sky-700"],
  review: [Star, "bg-yellow-100 text-yellow-700"],
  quote: [FileText, "bg-slate-100 text-slate-600"],
};
const STATUS = { active: "Attivo", suspended: "Sospeso", cancelled: "Cancellato", free: "Starter" };

const Info = ({ label, value, testid }) => (
  <div className="rounded-lg bg-slate-50 px-3 py-2">
    <p className="text-[11px] uppercase tracking-wider text-slate-400">{label}</p>
    <p className="text-sm font-semibold text-slate-900" data-testid={testid}>{value}</p>
  </div>
);

export default function UserHistorySheet({ userId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    if (userId) api.get(`/super-admin/users/${userId}/history`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [userId]);
  const u = data?.user;

  return (
    <Sheet open={!!userId} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent data-testid="user-history-sheet" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle data-testid="user-history-name">{u?.name || "Storico utente"}</SheetTitle>
          <SheetDescription>{u?.email}</SheetDescription>
        </SheetHeader>
        {!data && <div className="py-16 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>}
        {data?.error && <p className="py-10 text-center text-sm text-red-600">Impossibile caricare lo storico</p>}
        {u && (
          <>
            <div className="grid grid-cols-2 gap-2 mt-5">
              <Info label="Piano" value={u.plan_label} testid="user-history-plan" />
              <Info label="Stato" value={STATUS[u.subscription_status]} testid="user-history-status" />
              <Info label="Scadenza" value={u.scadenza_abbonamento ? fmtDate(u.scadenza_abbonamento) : "—"} testid="user-history-expiry" />
              <Info label="Iscrizione" value={fmtDate(u.created_at)} testid="user-history-signup" />
              <Info label="Incassato netto" value={eur(u.paid_total)} testid="user-history-paid" />
              <Info label="Preventivi · Accessi" value={`${u.quotes_count} · ${u.login_count}`} testid="user-history-counts" />
            </div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mt-6 mb-3">Cronologia ({data.events.length})</p>
            <ol className="relative border-l border-slate-200 ml-4 space-y-4" data-testid="user-history-timeline">
              {data.events.map((e, i) => {
                const [Icon, cls] = KINDS[e.kind] || KINDS.signup;
                return (
                  <li key={i} className="ml-6" data-testid={`history-event-${e.kind}`}>
                    <span className={`absolute -left-3.5 w-7 h-7 rounded-full flex items-center justify-center ring-4 ring-white ${cls}`}><Icon style={{ width: 13, height: 13 }} /></span>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-900">{e.title}</p>
                      {e.amount != null && <span className={`font-mono text-sm font-semibold ${e.amount < 0 ? "text-red-600" : "text-emerald-700"}`}>{e.amount < 0 ? "− " : ""}{eur(Math.abs(e.amount))}</span>}
                    </div>
                    {e.detail && <p className="text-xs text-slate-500 break-words">{e.detail}</p>}
                    <p className="text-[11px] text-slate-400 mt-0.5">{fmtDate(e.at)}</p>
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
